import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ChevronDown, LifeBuoy, Loader2, LogIn, MessageSquare, Send, ShieldCheck,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { API } from "../lib/api";
import { usePageDescription } from "../lib/usePageDescription";
import PageHero from "../components/PageHero";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";

const STATUS_META = {
  open: { label: "Offen", cls: "bg-amber-500/10 text-amber-300" },
  answered: { label: "Beantwortet", cls: "bg-emerald-500/10 text-emerald-300" },
  closed: { label: "Geschlossen", cls: "bg-zinc-500/10 text-zinc-400" },
};

const fmtDate = (iso) => new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function Support() {
  const { userToken, user } = useAuth();
  const pd = usePageDescription("support", {
    title: "Wir helfen dir",
    intro: "Egal ob Frage, Feedback oder Problem – schreib uns. Antworten landen direkt in deinem Habthek-Postfach.",
    keywords: ["Fragen", "Feedback", "Probleme", "Ideen"],
  });
  const { toast } = useToast();
  const [tickets, setTickets] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [reply, setReply] = useState("");
  const [replying, setReplying] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${userToken}` } };

  const load = async () => {
    try {
      const r = await axios.get(`${API}/support/mine`, auth);
      setTickets(r.data || []);
    } catch { setTickets([]); }
  };

  useEffect(() => {
    if (userToken) load();
    
  }, [userToken]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/support`, { subject: subject.trim(), message: message.trim() }, auth);
      setSubject(""); setMessage("");
      toast({ title: "Anfrage gesendet", description: "Das Team antwortet dir – du bekommst eine Nachricht in dein Postfach." });
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast({ title: "Fehler", description: typeof d === "string" ? d : "Senden fehlgeschlagen", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const sendReply = async (ticketId) => {
    if (!reply.trim()) return;
    setReplying(true);
    try {
      await axios.post(`${API}/support/${ticketId}/messages`, { text: reply.trim() }, auth);
      setReply("");
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast({ title: "Fehler", description: typeof d === "string" ? d : "Antwort fehlgeschlagen", variant: "destructive" });
    } finally { setReplying(false); }
  };

  return (
    <section className="mx-auto max-w-3xl px-4 py-14" data-testid="support-page">
      <PageHero
        eyebrow={pd.eyebrow || "Habthek Support"}
        icon={LifeBuoy}
        title={pd.title}
        keywords={pd.keywords}
        intro={pd.intro}
        testId="support-hero"
      />

      {!userToken ? (
        <div className="mt-8 rounded-2xl border border-white/10 bg-[#0f141b] p-8 text-center" data-testid="support-login-gate">
          <ShieldCheck className="mx-auto h-8 w-8 text-zinc-600" />
          <div className="mt-3 text-lg font-bold text-white">Bitte melde dich an</div>
          <p className="mt-1 text-sm text-zinc-500">Support-Anfragen laufen über deinen Habthek-Account, damit wir dir antworten können.</p>
          <div className="mt-5 flex justify-center gap-2">
            <Link to="/login"><Button className="btn-primary rounded-lg px-4 py-2 text-sm font-semibold"><LogIn className="mr-1.5 h-4 w-4" /> Einloggen</Button></Link>
            <Link to="/registrieren"><Button className="btn-ghost rounded-lg px-4 py-2 text-sm">Registrieren</Button></Link>
          </div>
        </div>
      ) : (
        <>
          {/* Neue Anfrage */}
          <form onSubmit={create} className="mt-8 rounded-2xl border border-white/10 bg-[#0f141b] p-6" data-testid="support-create-form">
            <div className="text-sm font-bold text-white">Neue Anfrage</div>
            <p className="mt-0.5 text-xs text-zinc-500">Du schreibst als <span className="text-zinc-300">{user?.habbo_name}</span></p>
            <Input
              data-testid="support-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Betreff (z.B. Frage zu meinem Account)"
              className="mt-4 bg-[#10141b] border-white/10 text-zinc-200"
            />
            <textarea
              data-testid="support-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Deine Nachricht an das Team…"
              className="mt-3 w-full rounded-xl border border-white/10 bg-[#10141b] p-4 text-sm text-zinc-200 outline-none placeholder:text-zinc-600 focus:border-[#9d7bff]/50"
            />
            <Button type="submit" disabled={busy || subject.trim().length < 3 || message.trim().length < 5} data-testid="support-submit" className="btn-primary mt-3 rounded-lg px-5 py-2 text-sm font-semibold">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Anfrage senden
            </Button>
          </form>

          {/* Meine Anfragen */}
          <div className="mt-8">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Meine Anfragen</div>
            {!tickets ? (
              <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>
            ) : tickets.length === 0 ? (
              <div className="mt-3 rounded-xl border border-white/10 bg-[#0f141b] px-4 py-8 text-center text-sm text-zinc-500" data-testid="support-empty">
                Noch keine Anfragen.
              </div>
            ) : (
              <div className="mt-3 space-y-2">
                {tickets.map((t) => {
                  const meta = STATUS_META[t.status] || STATUS_META.open;
                  const isOpen = expanded === t.id;
                  return (
                    <div key={t.id} className="overflow-hidden rounded-xl border border-white/10 bg-[#0f141b]" data-testid={`support-ticket-${t.id}`}>
                      <button
                        onClick={() => setExpanded(isOpen ? null : t.id)}
                        data-testid={`support-ticket-toggle-${t.id}`}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.03]"
                      >
                        <MessageSquare className="h-4 w-4 shrink-0 text-zinc-500" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-semibold text-white">{t.subject}</div>
                          <div className="text-[11px] text-zinc-500">{fmtDate(t.updated_at)} · {t.messages?.length || 0} Nachricht(en)</div>
                        </div>
                        <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${meta.cls}`}>{meta.label}</span>
                        <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      </button>
                      {isOpen && (
                        <div className="border-t border-white/5 px-4 py-3">
                          <div className="space-y-2.5">
                            {(t.messages || []).map((m) => (
                              <div key={m.id} className={`rounded-lg px-3 py-2 text-sm ${m.is_team ? "bg-[#7c4dff]/10 text-zinc-200" : "bg-white/5 text-zinc-300"}`}>
                                <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                                  {m.is_team ? "Habthek-Team" : m.author} · {fmtDate(m.created_at)}
                                </div>
                                <div className="whitespace-pre-wrap">{m.text}</div>
                              </div>
                            ))}
                          </div>
                          {t.status !== "closed" ? (
                            <div className="mt-3 flex gap-2">
                              <Input
                                data-testid={`support-reply-input-${t.id}`}
                                value={reply}
                                onChange={(e) => setReply(e.target.value)}
                                placeholder="Antworten…"
                                className="bg-[#10141b] border-white/10 text-zinc-200"
                              />
                              <Button onClick={() => sendReply(t.id)} disabled={replying || !reply.trim()} data-testid={`support-reply-send-${t.id}`} className="btn-primary rounded-lg px-3 py-2 text-sm">
                                {replying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                              </Button>
                            </div>
                          ) : (
                            <div className="mt-3 text-[11px] text-zinc-500">Diese Anfrage ist geschlossen.</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}
